import React, { useState, useEffect } from "react";
import type { FC } from "react";

/**
 * @typedef ModalUpdateStatusProps
 * @property {string} responseId - The ID of the response being updated.
 * @property {"correct" | "wrong" | "notMarked"} currentStatus - The current status of the response.
 * @property {boolean} isOpen - Determines if the modal is open.
 * @property {() => void} onClose - Function to close the modal.
 * @property {(responseId: string, status: string) => void} onConfirm - Function to confirm the status update.
 */
type ModalUpdateStatusProps = {
  responseId: string;
  currentStatus: "correct" | "wrong" | "notMarked";
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (responseId: string, status: string) => void;
};

/**
 * @description A modal component for updating the status of a student's response.
 * @param {ModalUpdateStatusProps} props - The props for the ModalUpdateStatus component.
 * @returns {JSX.Element} A modal that allows the user to update the status of a response.
 */
export const ModalUpdateStatus: FC<ModalUpdateStatusProps> = ({
  responseId,
  currentStatus,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);

  useEffect(() => {
    setSelectedStatus(currentStatus);
  }, [currentStatus]);

  const handleConfirm = () => {
    onConfirm(responseId, selectedStatus);
    onClose();
  };

  //! When the modal is open, we add the Bootstrap backdrop
  //! and the classes "fade show" to create the transition effect and dark background.
  return (
    <>
      {/* Dark backdrop only if the modal is open */}
      {isOpen && <div className="modal-backdrop fade show"></div>}

      <div
        className={`modal fade ${isOpen ? "show" : ""}`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={{ display: isOpen ? "block" : "none" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title mb-0">Atualizar Status da Resposta</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                title="Fechar"
                aria-label="Fechar"
              />
            </div>

            <div className="modal-body">
              <label className="form-label fw-semibold" htmlFor="status-select">
                Selecione o status:
              </label>
              <select
                id="status-select"
                className="form-select mt-1"
                value={selectedStatus}
                onChange={(e) =>
                  setSelectedStatus(
                    e.target.value as "correct" | "wrong" | "notMarked"
                  )
                }
              >
                <option value="correct">Correta</option>
                <option value="wrong">Incorreta</option>
              </select>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleConfirm}
              >
                Confirmar
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
